// Calendário das atividades: a SEMANA com as horas do dia nas linhas — a mesma
// grade da anotação de horas (pedido do Lucas, 09/10/2026: "utilize o
// calendário mais moderno que a anotação de horas utiliza"). Feito à mão como
// o de horas (grade CSS + blocos em posição absoluta), sem biblioteca nova.
//
// O que aparece:
//   · faixa "Sem data" no topo: atividade sem prazo NÃO pode sumir da visão —
//     era por isso que as do comercial "não apareciam" (21 de 27 sem data);
//   · faixa "dia todo" em cada dia: prazo sem hora marcada (meia-noite/23:59);
//   · bloco na hora marcada (30 min, lado a lado quando se sobrepõem);
//   · linha vermelha do agora, no fuso de São Paulo.
// Clicar num horário vazio cria a atividade já com aquela data e hora.
// As regras moram em calendarioUtils.ts (testadas); aqui só se desenha.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Segmented, Tag, Tooltip, Typography } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { diaDaSemana, formatarHorario } from '../operacional-horas/horasUtils';
import {
  agoraSaoPaulo, andar, atrasada, clienteEOportunidade, dataHoraDoClique, diasVisiveis, inicioDaVisao,
  montarCalendario, oportunidadeECliente, quandoDe, rotuloPeriodo, type Visao,
} from './calendarioUtils';
import { PRIORIDADE_LABEL, STATUS_LABEL } from './types';
import type { Atividade } from './types';

const { Text } = Typography;
const PX_H = 48;                // pixels por hora (o mesmo das horas)
const ALTURA = 24 * PX_H;
const VERMELHO = '#B91626';

interface Props {
  atividades: Atividade[];
  onEditar: (a: Atividade) => void;
  /** Clique num horário vazio: data/hora 'AAAA-MM-DDTHH:MM' para a atividade nova. */
  onCriar?: (dataHora: string) => void;
}

function useLargura() {
  const [w, setW] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));
  useEffect(() => {
    const f = () => setW(window.innerWidth);
    window.addEventListener('resize', f);
    return () => window.removeEventListener('resize', f);
  }, []);
  return w;
}

/** Texto do balão: tudo o que a atividade diz, em linhas. */
function dica(a: Atividade): React.ReactNode {
  const q = quandoDe(a.data_vencimento);
  const op = oportunidadeECliente(a);
  const quem = (a.atribuicoes || []).map(x => x.email.split('@')[0]).join(', ');
  return (
    <div style={{ fontSize: 12 }}>
      <div style={{ fontWeight: 600 }}>{a.titulo}</div>
      {op && <div>{op}</div>}
      {q && <div>{q.dia.slice(8, 10)}/{q.dia.slice(5, 7)}{q.minuto != null ? ` às ${formatarHorario(q.minuto)}` : ' · prazo do dia'}</div>}
      <div>{STATUS_LABEL[a.status]?.label || a.status}{quem ? ` · ${quem}` : ''}</div>
    </div>
  );
}

export default function CalendarioAtividades({ atividades, onEditar, onCriar }: Props) {
  const estreito = useLargura() < 768;
  const [visao, setVisao] = useState<Visao>(() => (estreito ? 'dia' : '7'));
  const [agora, setAgora] = useState(() => agoraSaoPaulo());
  const [inicio, setInicio] = useState(() => inicioDaVisao(agoraSaoPaulo().dia, estreito ? 'dia' : '7'));
  const rolagem = useRef<HTMLDivElement>(null);

  // O "agora" anda sozinho (a linha vermelha e o "atrasada" acompanham).
  useEffect(() => {
    const t = setInterval(() => setAgora(agoraSaoPaulo()), 60_000);
    return () => clearInterval(t);
  }, []);
  // Abre em 07:00 — ninguém quer ver a madrugada primeiro.
  useEffect(() => {
    if (rolagem.current) rolagem.current.scrollTop = 7 * PX_H - 8;
  }, []);

  const dias = useMemo(() => diasVisiveis(inicio, visao), [inicio, visao]);
  const { semData, porDia } = useMemo(() => montarCalendario(atividades, dias), [atividades, dias]);

  const mudarVisao = (v: Visao) => {
    // Mantém o dia de referência: o primeiro visível, ou hoje se ele estiver na tela.
    const ref = dias.includes(agora.dia) ? agora.dia : dias[0];
    setVisao(v);
    setInicio(inicioDaVisao(ref, v));
  };
  const irParaHoje = () => setInicio(inicioDaVisao(agora.dia, visao));
  const cols = `52px repeat(${dias.length}, minmax(0, 1fr))`;

  const corDe = (a: Atividade) => {
    const st = STATUS_LABEL[a.status] || { bg: '#EEF0F5', cor: '#333' };
    const borda = atrasada(a, agora) ? VERMELHO : (PRIORIDADE_LABEL[a.prioridade]?.cor || '#0930D0');
    return { fundo: st.bg, texto: st.cor, borda };
  };

  function aoClicarNaGrade(e: React.MouseEvent<HTMLDivElement>, dia: string) {
    if (!onCriar) return;
    if ((e.target as HTMLElement).closest('[data-bloco]')) return;
    const r = e.currentTarget.getBoundingClientRect();
    const minuto = ((e.clientY - r.top) / PX_H) * 60;
    onCriar(dataHoraDoClique(dia, minuto));
  }

  const chip = (a: Atividade, comCliente = true) => {
    const cor = corDe(a);
    const op = comCliente ? (a.cliente_nome || a.negocio_titulo) : null;
    return (
      <Tooltip key={a.id} title={dica(a)}>
        <div data-bloco onClick={() => onEditar(a)}
          style={{
            background: cor.fundo, borderLeft: `3px solid ${cor.borda}`, borderRadius: 4,
            fontSize: 11, padding: '1px 5px', marginBottom: 2, cursor: 'pointer', color: cor.texto,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
          <strong>{a.titulo}</strong>{op ? <span style={{ color: '#555' }}> · {op}</span> : null}
        </div>
      </Tooltip>
    );
  };

  return (
    <div>
      {/* Navegação: anda o tamanho da visão; "Hoje" volta para o agora. */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <Button size="small" icon={<LeftOutlined />} onClick={() => setInicio(andar(inicio, visao, -1))} />
        <Button size="small" onClick={irParaHoje}>Hoje</Button>
        <Button size="small" icon={<RightOutlined />} onClick={() => setInicio(andar(inicio, visao, 1))} />
        <Text strong style={{ fontSize: 14, color: '#020D7F', marginLeft: 4 }}>{rotuloPeriodo(dias)}</Text>
        <Segmented size="small" value={visao} onChange={(v) => mudarVisao(v as Visao)} style={{ marginLeft: 'auto' }}
          options={[{ label: 'Dia', value: 'dia' }, { label: '3 dias', value: '3' }, { label: 'Semana', value: '7' }]} />
      </div>

      {/* Sem data: não somem. Clicar abre a atividade para marcar o dia. */}
      {semData.length > 0 && (
        <div style={{ border: '1px dashed #C9CEDB', borderRadius: 8, padding: '6px 8px', marginBottom: 10, background: '#FAFBFD' }}>
          <Text style={{ fontSize: 12 }}>
            <strong>{semData.length} sem data</strong>
            <Text type="secondary" style={{ fontSize: 12 }}> — abra e marque o dia para aparecer na grade</Text>
          </Text>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6, maxHeight: 92, overflowY: 'auto' }}>
            {semData.map(a => (
              <Tooltip key={a.id} title={dica(a)}>
                <Tag onClick={() => onEditar(a)}
                  style={{ cursor: 'pointer', marginInlineEnd: 0, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis',
                           borderLeft: `3px solid ${corDe(a).borda}` }}>
                  {a.titulo}{a.cliente_nome || a.negocio_titulo ? ` · ${a.cliente_nome || a.negocio_titulo}` : ''}
                </Tag>
              </Tooltip>
            ))}
          </div>
        </div>
      )}

      <div style={{ border: '1px solid #E6E8F0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
        {/* Cabeçalho: dia e quantas atividades tem */}
        <div style={{ display: 'grid', gridTemplateColumns: cols, borderBottom: '1px solid #E6E8F0', background: '#FAFBFD' }}>
          <div />
          {dias.map(d => {
            const ehHoje = d === agora.dia;
            const tot = porDia.get(d)?.total || 0;
            return (
              <div key={d} style={{ padding: '6px 4px', textAlign: 'center', borderLeft: '1px solid #EEF0F5', lineHeight: 1.3 }}>
                <div style={{ fontWeight: ehHoje ? 700 : 500, color: ehHoje ? '#0930D0' : undefined }}>
                  {diaDaSemana(d)} {d.slice(8, 10)}/{d.slice(5, 7)}
                </div>
                <div style={{ fontSize: 11, color: tot ? '#333' : '#aaa' }}>
                  {tot ? `${tot} atividade${tot > 1 ? 's' : ''}` : '—'}
                </div>
              </div>
            );
          })}
        </div>

        {/* Faixa "dia todo": prazo do dia, sem hora marcada */}
        <div style={{ display: 'grid', gridTemplateColumns: cols, borderBottom: '1px solid #E6E8F0' }}>
          <div style={{ fontSize: 10, color: '#888', padding: '4px 2px', textAlign: 'right', lineHeight: 1.2 }}>dia<br />todo</div>
          {dias.map(d => (
            <div key={d} style={{ borderLeft: '1px solid #EEF0F5', padding: 3, minHeight: 28, maxHeight: 96, overflowY: 'auto' }}>
              {(porDia.get(d)?.diaTodo || []).map(a => chip(a))}
            </div>
          ))}
        </div>

        {/* Grade das 24 horas */}
        <div ref={rolagem} style={{ height: 'clamp(360px, calc(100vh - 430px), 900px)', overflowY: 'auto', position: 'relative' }}>
          <div style={{ display: 'grid', gridTemplateColumns: cols, height: ALTURA, position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              {Array.from({ length: 24 }, (_, h) => (
                <div key={h} style={{ position: 'absolute', top: h * PX_H - 7, right: 6, fontSize: 10, color: '#999' }}>
                  {h ? `${String(h).padStart(2, '0')}:00` : ''}
                </div>
              ))}
            </div>
            {dias.map(d => {
              const blocos = porDia.get(d)?.blocos || [];
              return (
                <div key={d} onClick={(e) => aoClicarNaGrade(e, d)}
                  style={{
                    position: 'relative', borderLeft: '1px solid #EEF0F5',
                    cursor: onCriar ? 'copy' : 'default',
                    backgroundColor: d === agora.dia ? '#FBFCFF' : undefined,
                    backgroundImage: 'repeating-linear-gradient(to bottom, #EEF0F5 0, #EEF0F5 1px, transparent 1px, transparent 48px)',
                  }}>
                  {blocos.map(b => {
                    const a = b.a;
                    const cor = corDe(a);
                    const alt = Math.max(22, ((b.fim - b.inicio) / 60) * PX_H - 2);
                    const op = clienteEOportunidade(a);
                    return (
                      <Tooltip key={b.id} title={dica(a)}>
                        <div data-bloco onClick={() => onEditar(a)}
                          style={{
                            position: 'absolute', top: (b.inicio / 60) * PX_H + 1, height: alt,
                            left: `calc(${(b.coluna / b.colunas) * 100}% + 2px)`, width: `calc(${100 / b.colunas}% - 4px)`,
                            background: cor.fundo, borderLeft: `3px solid ${cor.borda}`, borderRadius: 4,
                            padding: '2px 5px', fontSize: 11, lineHeight: 1.25, overflow: 'hidden', cursor: 'pointer',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.06)', color: cor.texto, zIndex: 1,
                          }}>
                          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <span style={{ color: '#666' }}>{formatarHorario(b.inicio)}</span> <strong>{a.titulo}</strong>
                          </div>
                          {alt > 30 && op && (
                            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#444' }}>{op}</div>
                          )}
                        </div>
                      </Tooltip>
                    );
                  })}

                  {agora.dia === d && (
                    <div style={{ position: 'absolute', left: 0, right: 0, top: (agora.minuto / 60) * PX_H, height: 0,
                                  borderTop: `2px solid ${VERMELHO}`, pointerEvents: 'none', zIndex: 2 }}>
                      <span style={{ position: 'absolute', left: -4, top: -5, width: 8, height: 8, borderRadius: '50%', background: VERMELHO }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
        {onCriar
          ? 'Clique num horário vazio para criar uma atividade naquele horário; clique numa atividade para abrir. Borda vermelha = atrasada.'
          : 'Clique numa atividade para abrir. Borda vermelha = atrasada.'}
      </Text>
    </div>
  );
}
